import {
    Badge,
    Drawer,
    InfoList,
    InfoListItem,
    MultiSegmentProgressBar,
} from '@moreharvest/component-library';
import { dropdownSelect } from '@services/analytics';
import { useScreenName } from '@hooks/useScreenName';
import { USDFormatter } from '@utils/currency';
import { FormattedMessage } from '@utils/i18n';
import dayjs from 'dayjs';
import { ComponentProps, MouseEvent, useRef } from 'react';
import { useIntl } from 'react-intl';
import { AnalyticsDropdownId, toJsArray } from 'mh-client';
import type * as MH from 'mh-client';

const BREAKDOWN_COLORS: Record<string, string> = {
    paid: 'bg-green-400',
    projected: 'bg-orange-400',
    fee: 'bg-neutral-400',
};

const BREAKDOWN_LABEL_IDS: Record<string, string> = {
    paid: 'dividends_breakdown_paid',
    projected: 'dividends_breakdown_pending',
    fee: 'dividends_breakdown_fees',
};

interface DividendPeriodFieldProps {
    period: MH.DividendPeriod;
}

type DrawerProps = ComponentProps<typeof Drawer>;

interface ExpandTrackingDrawerProps extends DrawerProps {
    onExpand: () => void;
}

/**
 * Wraps the library `Drawer`, which exposes no expand callback. Its whole header
 * toggles, so a header click is any click not inside the drawer's own content; an
 * open-state mirror (the drawer starts collapsed and only toggles on a header
 * click) fires `onExpand` once per expansion, never on collapse.
 *
 * Assumes the header is the sole toggle: a programmatic open/close or a control
 * inside the header region would desync the mirror.
 */
const ExpandTrackingDrawer = ({
    onExpand,
    children,
    ...drawerProps
}: ExpandTrackingDrawerProps) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const openRef = useRef(false);

    const handleHeaderClick = (event: MouseEvent) => {
        if (contentRef.current?.contains(event.target as Node)) return;
        openRef.current = !openRef.current;
        if (openRef.current) onExpand();
    };

    return (
        <div onClickCapture={handleHeaderClick}>
            <Drawer {...drawerProps}>
                <div ref={contentRef}>{children}</div>
            </Drawer>
        </div>
    );
};

const DividendPeriodField = ({ period }: DividendPeriodFieldProps) => {
    const intl = useIntl();
    const payouts = toJsArray(period.payouts);
    const breakdown = toJsArray(period.divEarningsBreakdown);
    const screenName = useScreenName();

    const firePeriodExpand = () => {
        if (!screenName) return;
        dropdownSelect({
            screen_name: screenName,
            dropdown_id: AnalyticsDropdownId.getInstance().DIVIDEND_HISTORY,
            value: dayjs(period.exDividendDate.value).format('YYYY'),
        });
    };

    const firePayoutExpand = (payout: MH.DividendPeriodPayout) => {
        if (!screenName) return;
        const dateValue =
            payout.status.name === 'paid'
                ? payout.payoutDate.value
                : payout.maturationDate.value;
        dropdownSelect({
            screen_name: screenName,
            dropdown_id: AnalyticsDropdownId.getInstance().DIVIDEND_PAYOUT,
            value: dayjs(dateValue).format('YYYY-MM-DD'),
        });
    };

    const segments = breakdown.map(entry => {
        const labelId = BREAKDOWN_LABEL_IDS[entry.type.name];
        return {
            value: entry.amount,
            color: BREAKDOWN_COLORS[entry.type.name] ?? 'bg-neutral-400',
            label: labelId
                ? intl.formatMessage({ id: labelId })
                : entry.type.name,
        };
    });

    const status = period.status.name;
    const periodBadge = (() => {
        if (status === 'paid') {
            return (
                <Badge
                    label={intl.formatMessage({ id: 'dividends_fully_paid' })}
                    color="green"
                />
            );
        }
        if (status === 'partially_paid') {
            const paidCount = payouts.filter(
                p => p.status.name === 'paid'
            ).length;
            return (
                <Badge
                    label={intl.formatMessage(
                        { id: 'dividends_x_of_y_paid' },
                        {
                            paidCount: String(paidCount),
                            totalCount: String(payouts.length),
                        }
                    )}
                    color="green"
                />
            );
        }
        return (
            <Badge
                label={intl.formatMessage(
                    { id: 'dividends_x_of_y_paid' },
                    {
                        paidCount: '0',
                        totalCount: String(payouts.length),
                    }
                )}
                color="neutral"
            />
        );
    })();

    return (
        <ExpandTrackingDrawer
            onExpand={firePeriodExpand}
            title={
                <div className="flex w-full justify-between items-center">
                    <h6 className="Body-Small font-semibold">
                        {dayjs(period.exDividendDate.value).format('MMM YYYY')}
                    </h6>
                    {payouts.length > 0 ? periodBadge : ''}
                </div>
            }
            chevronPosition="left"
        >
            <InfoList>
                <InfoListItem.WithDivider>
                    <label className="Body-Small">
                        <FormattedMessage id="dividends_ex_dividend_date" />
                    </label>
                    <p className="Body-Small semibold text-neutral-900 dark:text-neutral-950-dark">
                        {dayjs(period.exDividendDate.value).format(
                            'YYYY-MM-DD'
                        )}
                    </p>
                </InfoListItem.WithDivider>
                <InfoListItem.WithDivider>
                    <label className="Body-Small">
                        <FormattedMessage id="dividends_dividend_per_share_usd" />
                    </label>
                    <p className="Body-Small semibold text-neutral-900 dark:text-neutral-950-dark">
                        {USDFormatter.format(period.dividendPerShare)}
                    </p>
                </InfoListItem.WithDivider>
            </InfoList>
            <div className="p-4">
                <MultiSegmentProgressBar
                    title={intl.formatMessage({ id: 'dividends_total_earned' })}
                    totalValue={USDFormatter.format(
                        period.totalEarnedDivPeriod
                    )}
                    segments={segments}
                />
            </div>
            {payouts.length > 0 && (
                <h6 className="Body-Small font-semibold px-4 pt-2">
                    <FormattedMessage id="dividends_payouts" />
                </h6>
            )}
            {payouts.map((payout, idx) => {
                const payoutTitle =
                    payout.status.name === 'paid'
                        ? dayjs(payout.payoutDate.value).format('MMM D, YYYY')
                        : intl.formatMessage(
                              { id: 'dividends_matures_on' },
                              {
                                  date: dayjs(
                                      payout.maturationDate.value
                                  ).format('MMM D, YYYY'),
                              }
                          );
                return (
                    <div
                        className={`p-4 ${idx > 0 ? 'pt-0' : ''}`}
                        key={`${payout.payoutDate}-${idx}`}
                    >
                        <ExpandTrackingDrawer
                            onExpand={() => firePayoutExpand(payout)}
                            chevronPosition="left"
                            title={
                                <div className="flex w-full justify-between items-center">
                                    <h6 className="Body-Small font-semibold">
                                        {payoutTitle}
                                    </h6>
                                    {payout.status.name === 'paid' && (
                                        <Badge
                                            label={intl.formatMessage({
                                                id: 'dividends_paid',
                                            })}
                                            color="green"
                                        />
                                    )}
                                </div>
                            }
                        >
                            <PayoutDetails payout={payout} />
                        </ExpandTrackingDrawer>
                    </div>
                );
            })}
        </ExpandTrackingDrawer>
    );
};

const PayoutDetails = ({ payout }: { payout: MH.DividendPeriodPayout }) => {
    const dateLabel = payout.purchaseDate
        ? 'dividends_purchase_date'
        : 'dividends_dividend_start_date';
    const dateValue = payout.purchaseDate ?? payout.dividendStartDate ?? '';

    return (
        <InfoList>
            <InfoListItem.WithDivider size="small">
                <label className="Body-Small">
                    <FormattedMessage id={dateLabel} />
                </label>
                <p className="Body-Small semibold text-neutral-900 dark:text-neutral-950-dark">
                    {dayjs(dateValue).format('YYYY-MM-DD')}
                </p>
            </InfoListItem.WithDivider>
            <InfoListItem.WithDivider size="small">
                <label className="Body-Small">
                    <FormattedMessage id="dividend_total_dividend_earned_usd" />
                </label>
                <p className="Body-Small semibold text-neutral-900 dark:text-neutral-950-dark">
                    {USDFormatter.format(payout.earned)}
                </p>
            </InfoListItem.WithDivider>
            <InfoListItem.WithDivider size="small">
                <label className="Body-Small">
                    <FormattedMessage id="bank_transfer_fee_usd" />
                </label>
                <p className="Body-Small semibold text-neutral-900 dark:text-neutral-950-dark">
                    -{USDFormatter.format(payout.fee)}
                </p>
            </InfoListItem.WithDivider>
            <InfoListItem.WithDivider size="small">
                <label className="Body-Small">
                    {payout.paidOn ? (
                        <FormattedMessage
                            id="dividends_paid_on"
                            values={{
                                date: dayjs(payout.paidOn).format(
                                    'MMM D, YYYY'
                                ),
                            }}
                        />
                    ) : (
                        <FormattedMessage id="you_receive_usd" />
                    )}
                </label>
                <p className="Body-Small semibold font-bold text-neutral-900 dark:text-neutral-950-dark">
                    {USDFormatter.format(payout.received)}
                </p>
            </InfoListItem.WithDivider>
        </InfoList>
    );
};

export default DividendPeriodField;
